import * as THREE from 'three';
import type { System } from '../../systems';
import type { EntityRegistry } from '../../entities';
import type { Landable } from '../sector';
import type { PlayerShip } from '../ships';
import { FACTIONS } from '../factions';
import type { FactionId } from '../factions';
import { NpcShip } from './NpcShip';
import type { NpcRole } from './NpcShip';
import { MiningBeam } from './MiningBeam';
import { MinerBrain } from './MinerBrain';
import { GuardBrain } from './GuardBrain';
import {
  createAsteroidClaims,
  type NpcBrain,
  type NpcBrainContext,
  type AsteroidClaims,
  type PatrolBeat,
  type PatrolBeatProvider,
  type PatrolHub
} from './brain';
import type { HostileProjectiles } from './HostileProjectiles';
import type { Explosions } from '../combat';
import { EngineTrail } from '../../visual';
import { NpcHealthBar } from './NpcHealthBar';

interface NpcEntry {
  readonly ship: NpcShip;
  readonly brain: NpcBrain;
  readonly beam: MiningBeam | null;
  readonly trails: EngineTrail[];
  healthBar: NpcHealthBar | null;
  /** Accumulated dt waiting for the next brain tick — keeps simulation correct
   *  when we throttle distant NPCs to 30/15/7.5 Hz instead of 60. */
  brainAccumulator: number;
  /** When true, the cleanup loop skips this entry — the brain's
   *  `finished` flag is ignored. Mission-tagged ships (tutorial
   *  pirate, cargo hauler) use this so they don't despawn while the
   *  player is still trying to find them. */
  missionProtected: boolean;
}

export interface NpcDirectorDependencies {
  readonly entities: EntityRegistry;
  readonly root: THREE.Object3D;
  readonly camera: THREE.Camera;
  readonly stationPos: THREE.Vector3;
  readonly landables: readonly Landable[];
  readonly getPlayerShip: () => PlayerShip | null;
  readonly hostileProjectiles: HostileProjectiles;
  readonly explosions: Explosions;
}

const MINER_TARGET = 110;
const GUARD_TARGET = 45;
// New ships always spawn well outside the player's combat horizon so a
// freshly-killed guard never reappears in the player's lap. Combined with
// the brain instance being recreated, this is what makes respawns feel
// "without memory" — the NPC has no idea the previous fight happened.
const MIN_SPAWN_DIST_FROM_PLAYER = 1400;
const SPAWN_OFFSET_MIN = 140;
const SPAWN_OFFSET_MAX = 320;
/** Station presence quotas. The station only retains a small "always-on"
 *  patrol so the player never feels alone while docked — every other NPC
 *  is dispatched to a planet hub so density spreads across the system
 *  instead of piling 60+ ships on top of the spawn area. */
const STATION_PATROL_GUARDS = 8;
const STATION_PATROL_MINERS = 3;
const STATION_PRESENCE_RADIUS = 600;
const STATION_PRESENCE_RADIUS_SQ =
  STATION_PRESENCE_RADIUS * STATION_PRESENCE_RADIUS;

const GUARD_HULL_MAX = 40;
const MINER_HULL_MAX = 28;

// When a guard is attacked, only nearby guards (visual range) join the fight —
// no more system-wide alerts that turned every patrol into a hostile swarm.
const GUARD_BACKUP_RADIUS = 600;
// When a miner is attacked, guards within sensor range of the miner become hostile.
const MINER_DISTRESS_RADIUS = GuardBrain.DETECTION_RANGE;

// Distance-based AI tick rate. Hostile NPCs always run at full rate so combat
// stays responsive — these tiers only kick in for ambient miners/patrols out
// of the player's immediate sight, where 7.5 Hz steering is invisible.
const LOD_NEAR_DIST_SQ = 1500 * 1500;     // < 1500u : full 60 Hz
const LOD_MID_DIST_SQ = 3500 * 3500;      // < 3500u : 30 Hz
const LOD_FAR_DIST_SQ = 7000 * 7000;      // < 7000u : 15 Hz, beyond : 7.5 Hz
const LOD_INTERVAL_MID = 1 / 30;
const LOD_INTERVAL_FAR = 1 / 15;
const LOD_INTERVAL_DEEP = 1 / 7.5;

// Population check is light, but doing it every frame is wasted work — once a
// second is more than enough to keep guards/miners topped up.
const POPULATION_TICK_INTERVAL = 1.0;
// Cap on how many of each role we can spawn in one tick, so killing a wave
// doesn't crater the frame with a sudden burst of NpcShip constructions.
const MAX_SPAWNS_PER_TICK = 4;
// Number of hub-pick attempts before tryRespawn falls back to dropping the
// far-from-player gate. With 5 hubs, 6 attempts almost always lands a slot.
const MAX_SPAWN_ATTEMPTS = 6;

// NPCs within this distance from the camera are always drawn — avoids any
// pop when the player whips the camera around.
const NPC_CULL_NEAR_DIST = 250;
const NPC_CULL_NEAR_DIST_SQ = NPC_CULL_NEAR_DIST * NPC_CULL_NEAR_DIST;
// Slack added to the bounding sphere so engine plume tails (which extend
// behind the ship origin) don't pop as the ship enters the view frustum.
const NPC_CULL_RADIUS_PADDING = 12;

const GUARD_COLOR_PALETTE = [
  0x3f7fc4, 0x2a9d8f, 0x5a6cff, 0x4a6ea8, 0xa9c5ec, 0x2c3e50, 0x355e8a,
  0x6ac0ff, 0x8fb8d4
];
const MINER_COLOR_PALETTE = [
  0xf0a23c, 0xd85f2a, 0xc7922b, 0xb45d18, 0xe6c46e, 0x9a6a2f, 0xd88f3f,
  0xba4e12, 0xcea764
];

export class NpcDirector implements System {
  readonly id = 'npc-director';

  private readonly entries = new Map<number, NpcEntry>();
  private readonly claims: AsteroidClaims;
  private readonly tmpSpawn = new THREE.Vector3();
  private readonly tmpEngineWorld = new THREE.Vector3();
  private readonly tmpPlayerPos = new THREE.Vector3();
  private readonly tmpCameraPos = new THREE.Vector3();
  private readonly cullFrustum = new THREE.Frustum();
  private readonly cullProjView = new THREE.Matrix4();
  private readonly cullSphere = new THREE.Sphere();
  private populationTimer = 0;
  private initialized = false;
  private patrolRoster!: PatrolRoster;

  constructor(private readonly deps: NpcDirectorDependencies) {
    this.claims = createAsteroidClaims();
  }

  init(): void {
    this.patrolRoster = this.buildPatrolRoster();
    this.populateInitial();
    this.initialized = true;
  }

  /** Hubs = station + every landable (planet/moon). The roster derives
   *  every patrol beat from this list, so coverage automatically scales
   *  with whatever bodies the sector builder produced. */
  private buildPatrolRoster(): PatrolRoster {
    const hubs: PatrolHub[] = [];
    const stationPos = this.deps.stationPos;
    hubs.push({
      getWorldPosition: (out) => out.copy(stationPos)
    });
    for (const landable of this.deps.landables) {
      hubs.push(landable);
    }
    return new PatrolRoster(hubs);
  }

  /** Called by CombatSystem hook when the player hits an NPC. */
  onNpcAttackedByPlayer(victim: NpcShip): void {
    const victimEntry = this.entries.get(victim.id as unknown as number);
    if (victimEntry) {
      victimEntry.brain.onAttackedByPlayer();
    }

    if (victim.role === 'guard') {
      // Attacking a guard alerts every guard within backup radius.
      this.alertGuardsNear(victim.object3D.position, GUARD_BACKUP_RADIUS);
    } else if (victim.role === 'miner') {
      // Attacking a miner alerts guards within vision of the distress event.
      this.alertGuardsNear(victim.object3D.position, MINER_DISTRESS_RADIUS);
    }
    // role === 'pirate': intentional no-op. Tutorial pirate ships are
    // explicitly outside the guard/miner protection web — destroying
    // them is not a crime and guards don't come running.
  }

  resetHostility(): void {
    for (const entry of this.entries.values()) {
      entry.brain.clearHostility();
    }
  }

  /** Spawn a one-shot tutorial pirate at the given position. Reuses
   *  the standard guard combat brain in pirate mode (hostile from
   *  spawn, no patrol return). Returns the live ship so the mission
   *  director can track it for marker + destruction events. */
  spawnTutorialPirate(position: THREE.Vector3): NpcShip {
    const ship = new NpcShip({
      // The pirate borrows the guards' fighter silhouette but with a
      // dark hull so it reads as "not one of yours" at a glance.
      factionId: 'guards',
      primaryColor: 0x18181f,
      role: 'pirate',
      hullMax: GUARD_HULL_MAX,
      position
    });
    this.deps.entities.add(ship);
    const trails = this.createTrails(ship, 'pirate');

    const ctx: NpcBrainContext = {
      ship,
      stationPos: this.deps.stationPos,
      landables: this.deps.landables,
      entities: this.deps.entities,
      getPlayerShip: this.deps.getPlayerShip,
      beam: null,
      hostileProjectiles: this.deps.hostileProjectiles,
      claims: this.claims,
      explosions: this.deps.explosions,
      patrolBeats: this.patrolRoster
    };
    const brain = new GuardBrain(ctx, {
      patrolWaypoints: 0,
      hostileFromSpawn: true,
      noPatrolReturn: true
    });

    this.entries.set(ship.id as unknown as number, {
      ship,
      brain,
      beam: null,
      trails,
      healthBar: null,
      brainAccumulator: 0,
      // Tutorial pirate is mission-protected — never auto-despawn
      // even if the brain's `finished` flips for any reason.
      missionProtected: true
    });
    return ship;
  }

  /** Spawn a dedicated mission cargo miner at the given position.
   *  Effectively-infinite quota so the brain never returns to the
   *  station mid-mission, plus mission protection so the cleanup
   *  loop never despawns it. Tutorial-only — ordinary spawns still
   *  go through `spawnNpc`. */
  spawnTutorialCargoMiner(position: THREE.Vector3): NpcShip {
    const factionId: FactionId = 'miners';
    const faction = FACTIONS[factionId];
    const palette = MINER_COLOR_PALETTE;
    const color =
      palette[Math.floor(Math.random() * palette.length)] ??
      faction.defaultColor;
    const ship = new NpcShip({
      factionId,
      primaryColor: color,
      role: 'miner',
      hullMax: MINER_HULL_MAX,
      position
    });
    this.deps.entities.add(ship);

    const beam = new MiningBeam(this.deps.camera);
    this.deps.root.add(beam.object3D);
    const trails = this.createTrails(ship, 'miner');

    const ctx: NpcBrainContext = {
      ship,
      stationPos: this.deps.stationPos,
      landables: this.deps.landables,
      entities: this.deps.entities,
      getPlayerShip: this.deps.getPlayerShip,
      beam,
      hostileProjectiles: this.deps.hostileProjectiles,
      claims: this.claims,
      explosions: this.deps.explosions,
      patrolBeats: this.patrolRoster
    };
    const brain = new MinerBrain(ctx, { maxAsteroidsToMine: 999 });

    this.entries.set(ship.id as unknown as number, {
      ship,
      brain,
      beam,
      trails,
      healthBar: null,
      brainAccumulator: 0,
      missionProtected: true
    });
    return ship;
  }

  /** Snapshot of every live NPC for the radar/minimap. The caller decides how
   *  to colour each blip from `hostile` and `role`. Returns the NPCs' own
   *  Vector3 references (read-only — do not mutate). */
  forEachRadarTarget(
    callback: (
      position: THREE.Vector3,
      hostile: boolean,
      role: NpcRole
    ) => void
  ): void {
    for (const entry of this.entries.values()) {
      if (!entry.ship.isAlive) continue;
      callback(
        entry.ship.object3D.position,
        entry.brain.isHostile(),
        entry.ship.role
      );
    }
  }

  update(dt: number): void {
    if (!this.initialized) return;

    const player = this.deps.getPlayerShip();
    const havePlayer = player !== null;
    if (havePlayer) this.tmpPlayerPos.copy(player!.object3D.position);

    // Rebuild the camera frustum once per frame so we can hide off-screen
    // NPCs. Their engine plumes have frustumCulled=false individually (vertex
    // shaders move geometry), so without this manual gate every NPC's plume
    // is drawn even when behind the player. Hiding the parent Object3D skips
    // the whole subtree — the brain still ticks, only rendering is paused.
    this.deps.camera.updateMatrixWorld();
    this.deps.camera.getWorldPosition(this.tmpCameraPos);
    const persp = this.deps.camera as THREE.PerspectiveCamera;
    this.cullProjView.multiplyMatrices(
      persp.projectionMatrix,
      persp.matrixWorldInverse
    );
    this.cullFrustum.setFromProjectionMatrix(this.cullProjView);

    const toRemove: number[] = [];
    for (const [id, entry] of this.entries) {
      if (!entry.ship.isAlive) {
        toRemove.push(id);
        continue;
      }

      // Hostile NPCs (combat) always tick at full rate — distant ambient ships
      // tick less often, and the brain receives the accumulated dt so steering
      // and timers stay correct.
      entry.brainAccumulator += dt;
      const interval = this.brainTickInterval(entry, havePlayer);
      if (interval <= 0 || entry.brainAccumulator >= interval) {
        entry.brain.update(entry.brainAccumulator);
        entry.brainAccumulator = 0;
      }

      const visible = this.computeVisibility(entry);
      if (entry.ship.object3D.visible !== visible) {
        entry.ship.object3D.visible = visible;
      }
      if (entry.healthBar && entry.healthBar.object3D.visible !== visible) {
        entry.healthBar.object3D.visible = visible;
      }

      // Trails/health-bar updates touch GPU-side state — only run them when
      // the NPC is actually being drawn this frame.
      if (visible) {
        this.updateTrails(entry, dt);
        this.updateHealthBar(entry);
      }
      if (entry.brain.finished && !entry.missionProtected) {
        entry.ship.markForRemoval();
        toRemove.push(id);
      }
    }

    for (const id of toRemove) {
      const entry = this.entries.get(id);
      if (entry) this.cleanupEntry(entry);
      this.entries.delete(id);
    }

    this.populationTimer -= dt;
    if (this.populationTimer <= 0) {
      this.maintainPopulation();
      this.populationTimer = POPULATION_TICK_INTERVAL;
    }
  }

  /** Whether this NPC should be drawn this frame. Anything within a small
   *  near-radius of the camera stays visible regardless of frustum (avoids
   *  any pop on fast camera rotations); everything else is sphere-tested
   *  against the projection frustum we built at the top of update(). */
  private computeVisibility(entry: NpcEntry): boolean {
    const ship = entry.ship;
    const dsq = ship.object3D.position.distanceToSquared(this.tmpCameraPos);
    if (dsq < NPC_CULL_NEAR_DIST_SQ) return true;
    this.cullSphere.center.copy(ship.object3D.position);
    this.cullSphere.radius = ship.collisionRadius + NPC_CULL_RADIUS_PADDING;
    return this.cullFrustum.intersectsSphere(this.cullSphere);
  }

  /** AI tick interval for an entry. 0 means "tick this frame". Hostile and
   *  near-camera NPCs run at 60 Hz; everything else scales down with distance
   *  so 100+ ambient ships don't all think every frame. */
  private brainTickInterval(entry: NpcEntry, havePlayer: boolean): number {
    if (entry.brain.isHostile()) return 0;
    if (!havePlayer) return 0;
    const dsq = entry.ship.object3D.position.distanceToSquared(this.tmpPlayerPos);
    if (dsq < LOD_NEAR_DIST_SQ) return 0;
    if (dsq < LOD_MID_DIST_SQ) return LOD_INTERVAL_MID;
    if (dsq < LOD_FAR_DIST_SQ) return LOD_INTERVAL_FAR;
    return LOD_INTERVAL_DEEP;
  }

  private updateHealthBar(entry: NpcEntry): void {
    // Once the NPC has entered combat, keep the bar visible until death/despawn.
    if (!entry.healthBar && entry.brain.isHostile()) {
      const bar = new NpcHealthBar();
      this.deps.root.add(bar.object3D);
      entry.healthBar = bar;
    }
    const bar = entry.healthBar;
    if (!bar) return;

    const ship = entry.ship;
    bar.setRatio(ship.hull / ship.hullMax);
    const offsetY = ship.collisionRadius + 1.6;
    bar.positionAbove(
      ship.object3D.position,
      offsetY,
      this.deps.camera.quaternion
    );
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.cleanupEntry(entry);
      entry.ship.markForRemoval();
    }
    this.entries.clear();
  }

  private updateTrails(entry: NpcEntry, dt: number): void {
    if (entry.trails.length === 0) return;
    const ship = entry.ship;
    const thrust = clamp01(ship.velocity.length() / 50);
    const thrustOn = thrust > 0.08;
    if (thrustOn) {
      ship.object3D.updateMatrixWorld();
      const limit = Math.min(entry.trails.length, ship.engines.length);
      for (let i = 0; i < limit; i++) {
        const engine = ship.engines[i];
        const trail = entry.trails[i];
        if (!engine || !trail) continue;
        this.tmpEngineWorld
          .copy(engine.local)
          .applyMatrix4(ship.object3D.matrixWorld);
        trail.emit(this.tmpEngineWorld, thrust, dt);
      }
    }
    for (const trail of entry.trails) trail.update(dt);
  }

  private cleanupEntry(entry: NpcEntry): void {
    entry.brain.dispose();
    if (entry.beam) {
      entry.beam.dispose();
      this.deps.root.remove(entry.beam.object3D);
    }
    for (const trail of entry.trails) {
      this.deps.root.remove(trail.object3D);
      trail.dispose();
    }
    if (entry.healthBar) {
      this.deps.root.remove(entry.healthBar.object3D);
      entry.healthBar.dispose();
      entry.healthBar = null;
    }
  }

  private alertGuardsNear(
    position: THREE.Vector3,
    radius: number
  ): void {
    const radiusSq = radius * radius;
    for (const entry of this.entries.values()) {
      if (entry.ship.role !== 'guard') continue;
      if (entry.brain.isHostile()) continue;
      const dsq = entry.ship.object3D.position.distanceToSquared(position);
      if (dsq <= radiusSq) {
        entry.brain.onAttackedByPlayer();
      }
    }
  }

  private populateInitial(): void {
    for (let i = 0; i < MINER_TARGET; i++) {
      this.tryRespawn('miner', false);
    }
    for (let i = 0; i < GUARD_TARGET; i++) {
      this.tryRespawn('guard', false);
    }
  }

  /** Refill the population. Spawns up to MAX_SPAWNS_PER_TICK per role each
   *  pass so killing a wave of NPCs doesn't leave a lingering deficit, and
   *  retries inside tryRespawn so a single far-from-player gate failure
   *  never silently drops the slot. */
  private maintainPopulation(): void {
    const counts = this.countByRole();
    const minerDeficit = MINER_TARGET - counts.miner;
    const guardDeficit = GUARD_TARGET - counts.guard;

    const minerBurst = Math.min(Math.max(0, minerDeficit), MAX_SPAWNS_PER_TICK);
    for (let i = 0; i < minerBurst; i++) this.tryRespawn('miner', true);

    const guardBurst = Math.min(Math.max(0, guardDeficit), MAX_SPAWNS_PER_TICK);
    for (let i = 0; i < guardBurst; i++) this.tryRespawn('guard', true);
  }

  /** Wrap spawnAtRandomHub with retries so silent failures (e.g., every
   *  candidate hub happens to be inside MIN_SPAWN_DIST_FROM_PLAYER on this
   *  tick) don't leave a permanent population gap. */
  private tryRespawn(role: NpcRole, preferFarFromPlayer: boolean): boolean {
    for (let attempt = 0; attempt < MAX_SPAWN_ATTEMPTS; attempt++) {
      if (this.spawnAtRandomHub(role, preferFarFromPlayer)) return true;
    }
    // Last-ditch: drop the far-from-player constraint so we always land a
    // spawn rather than starving the population. This only kicks in when
    // every hub is within the safe radius — usually means the player is
    // bouncing around the inner system.
    if (preferFarFromPlayer) {
      return this.spawnAtRandomHub(role, false);
    }
    return false;
  }

  private countByRole(): { miner: number; guard: number } {
    let miner = 0;
    let guard = 0;
    for (const entry of this.entries.values()) {
      // Pirates aren't part of the population pipeline, so their
      // count never feeds the maintain-population deltas.
      if (entry.ship.role === 'pirate') continue;
      if (entry.ship.role === 'miner') miner++;
      else if (entry.ship.role === 'guard') guard++;
    }
    return { miner, guard };
  }

  private spawnAtRandomHub(
    role: NpcRole,
    preferFarFromPlayer: boolean
  ): boolean {
    const player = this.deps.getPlayerShip();
    const playerPos = player?.object3D.position ?? null;

    const stationHub = this.deps.stationPos.clone();
    const planetHubs: THREE.Vector3[] = [];
    for (const landable of this.deps.landables) {
      const p = new THREE.Vector3();
      landable.getWorldPosition(p);
      planetHubs.push(p);
    }

    // Station gets a fixed patrol quota; everything else is dispatched to a
    // random planet hub. This replaces the old probabilistic bias that piled
    // most of the population around the station and tanked frame rate while
    // docked.
    const presenceTarget =
      role === 'guard' ? STATION_PATROL_GUARDS : STATION_PATROL_MINERS;
    const stationCount = this.countNearStation(role);
    let chosen: THREE.Vector3 | null = null;
    if (stationCount < presenceTarget) {
      const stationOk =
        !preferFarFromPlayer ||
        !playerPos ||
        stationHub.distanceTo(playerPos) > MIN_SPAWN_DIST_FROM_PLAYER;
      if (stationOk) chosen = stationHub;
    }
    if (!chosen) {
      // Hub must be far enough that *any* offset placement still lands outside
      // MIN_SPAWN_DIST_FROM_PLAYER — otherwise the strict gate below kicks in
      // and we silently drop the spawn.
      const safeMin = MIN_SPAWN_DIST_FROM_PLAYER + SPAWN_OFFSET_MAX;
      const pool =
        preferFarFromPlayer && playerPos
          ? planetHubs.filter((h) => h.distanceTo(playerPos) > safeMin)
          : planetHubs;
      const fallback = pool.length > 0 ? pool : planetHubs;
      chosen = fallback[Math.floor(Math.random() * fallback.length)] ?? stationHub;
    }
    if (!chosen) return false;

    // Bias the offset away from the player when far-spawning so the random
    // direction never accidentally lands the new NPC on top of the player.
    const offset =
      preferFarFromPlayer && playerPos
        ? randomOffsetAwayFrom(playerPos, chosen, SPAWN_OFFSET_MIN, SPAWN_OFFSET_MAX)
        : randomOffset(SPAWN_OFFSET_MIN, SPAWN_OFFSET_MAX);
    this.tmpSpawn.copy(chosen).add(offset);

    if (playerPos && preferFarFromPlayer) {
      const dist = this.tmpSpawn.distanceTo(playerPos);
      if (dist < MIN_SPAWN_DIST_FROM_PLAYER) return false;
    }

    this.spawnNpc(role, this.tmpSpawn);
    return true;
  }

  private countNearStation(role: NpcRole): number {
    let count = 0;
    const stationPos = this.deps.stationPos;
    for (const entry of this.entries.values()) {
      if (entry.ship.role !== role) continue;
      const dsq = entry.ship.object3D.position.distanceToSquared(stationPos);
      if (dsq <= STATION_PRESENCE_RADIUS_SQ) count++;
    }
    return count;
  }

  private spawnNpc(role: NpcRole, position: THREE.Vector3): void {
    const factionId: FactionId = role === 'guard' ? 'guards' : 'miners';
    const faction = FACTIONS[factionId];
    const palette =
      role === 'guard' ? GUARD_COLOR_PALETTE : MINER_COLOR_PALETTE;
    const color =
      palette[Math.floor(Math.random() * palette.length)] ?? faction.defaultColor;

    const ship = new NpcShip({
      factionId,
      primaryColor: color,
      role,
      hullMax: role === 'guard' ? GUARD_HULL_MAX : MINER_HULL_MAX,
      position
    });
    this.deps.entities.add(ship);

    let beam: MiningBeam | null = null;
    if (role === 'miner') {
      beam = new MiningBeam(this.deps.camera);
      this.deps.root.add(beam.object3D);
    }

    const trails = this.createTrails(ship, role);

    const ctx: NpcBrainContext = {
      ship,
      stationPos: this.deps.stationPos,
      landables: this.deps.landables,
      entities: this.deps.entities,
      getPlayerShip: this.deps.getPlayerShip,
      beam,
      hostileProjectiles: this.deps.hostileProjectiles,
      claims: this.claims,
      explosions: this.deps.explosions,
      patrolBeats: this.patrolRoster
    };

    const brain: NpcBrain =
      role === 'guard'
        ? new GuardBrain(ctx, { patrolWaypoints: 4 })
        : new MinerBrain(ctx, {
            maxAsteroidsToMine: 3 + Math.floor(Math.random() * 2)
          });

    this.entries.set(ship.id as unknown as number, {
      ship,
      brain,
      beam,
      trails,
      healthBar: null,
      // Random phase so the LOD-throttled tier of NPCs doesn't all think on
      // the same frame, which would cause periodic AI cost spikes.
      brainAccumulator: Math.random() * LOD_INTERVAL_DEEP,
      missionProtected: false
    });
  }

  private createTrails(_ship: NpcShip, _role: NpcRole): EngineTrail[] {
    // Detached ribbon trails replaced by the per-engine plasma flame layers
    // built into NpcShip via enhanceEngineFlames — visually consistent with
    // the player ship and avoids the world-space ribbon edge cases.
    return [];
  }
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function randomOffset(min: number, max: number): THREE.Vector3 {
  const theta = Math.random() * Math.PI * 2;
  const u = Math.random() * 2 - 1;
  const sinPhi = Math.sqrt(1 - u * u);
  const r = min + Math.random() * (max - min);
  return new THREE.Vector3(
    r * sinPhi * Math.cos(theta),
    r * u * 0.55,
    r * sinPhi * Math.sin(theta)
  );
}

/** Like randomOffset but always points away from `from` so the resulting
 *  spawn position lands on the far side of the hub relative to the player. */
function randomOffsetAwayFrom(
  from: THREE.Vector3,
  hub: THREE.Vector3,
  min: number,
  max: number
): THREE.Vector3 {
  const dx = hub.x - from.x;
  const dz = hub.z - from.z;
  const len = Math.sqrt(dx * dx + dz * dz) || 1;
  const baseTheta = Math.atan2(dz / len, dx / len);
  // Cone around the away vector: ±60°, so we still get spatial variety
  // without ever swinging back toward the player.
  const theta = baseTheta + (Math.random() - 0.5) * (Math.PI * 0.66);
  const u = Math.random() * 2 - 1;
  const sinPhi = Math.sqrt(1 - u * u);
  const r = min + Math.random() * (max - min);
  return new THREE.Vector3(
    r * sinPhi * Math.cos(theta),
    r * u * 0.55,
    r * sinPhi * Math.sin(theta)
  );
}

// Coverage targets per hub / lane. With 5 hubs and 10 lanes the floor is
// 5*3 + 10*2 = 35 of GUARD_TARGET=45 — the rest cycles through anchor
// reinforcement so under-defended areas naturally re-balance over time.
const TARGET_ANCHOR_PER_HUB = 3;
const TARGET_LANE_GUARDS = 2;

/**
 * Round-robin patrol assignment. Tracks how many guards are committed to
 * each hub (anchor) and each pair of hubs (lane), and hands out the most
 * undermanned slot whenever a guard requests one. Identity for release is
 * by hub object reference, so callers must pass back the exact beat they
 * acquired — not a copy.
 */
class PatrolRoster implements PatrolBeatProvider {
  private readonly hubs: PatrolHub[];
  private readonly anchorCounts: number[];
  private readonly lanes: { from: PatrolHub; to: PatrolHub }[];
  private readonly laneCounts: number[];

  constructor(hubs: PatrolHub[]) {
    this.hubs = hubs;
    this.anchorCounts = new Array(hubs.length).fill(0);
    this.lanes = [];
    for (let i = 0; i < hubs.length; i++) {
      for (let j = i + 1; j < hubs.length; j++) {
        const a = hubs[i];
        const b = hubs[j];
        if (a && b) this.lanes.push({ from: a, to: b });
      }
    }
    this.laneCounts = new Array(this.lanes.length).fill(0);
  }

  acquire(): PatrolBeat {
    // 1) Fill anchor coverage first — every hub gets its quota before any
    //    lane gets a guard. Otherwise the first wave would scatter onto
    //    lanes and leave hubs empty.
    let bestAnchorIdx = -1;
    let bestAnchor = TARGET_ANCHOR_PER_HUB;
    for (let i = 0; i < this.anchorCounts.length; i++) {
      const count = this.anchorCounts[i] ?? 0;
      if (count < bestAnchor) {
        bestAnchor = count;
        bestAnchorIdx = i;
      }
    }
    if (bestAnchorIdx >= 0) {
      this.anchorCounts[bestAnchorIdx] =
        (this.anchorCounts[bestAnchorIdx] ?? 0) + 1;
      const hub = this.hubs[bestAnchorIdx];
      if (hub) return { kind: 'anchor', hub };
    }

    // 2) Then fill lane coverage — guards shuttle between hub pairs and
    //    intercept anything moving along those routes.
    let bestLaneIdx = -1;
    let bestLane = TARGET_LANE_GUARDS;
    for (let i = 0; i < this.laneCounts.length; i++) {
      const count = this.laneCounts[i] ?? 0;
      if (count < bestLane) {
        bestLane = count;
        bestLaneIdx = i;
      }
    }
    if (bestLaneIdx >= 0) {
      this.laneCounts[bestLaneIdx] = (this.laneCounts[bestLaneIdx] ?? 0) + 1;
      const lane = this.lanes[bestLaneIdx];
      if (lane) return { kind: 'lane', from: lane.from, to: lane.to };
    }

    // 3) Overflow: pile onto the least-anchored hub so reinforcement is
    //    distributed evenly even past quota.
    let leastIdx = 0;
    for (let i = 1; i < this.anchorCounts.length; i++) {
      if ((this.anchorCounts[i] ?? 0) < (this.anchorCounts[leastIdx] ?? 0)) {
        leastIdx = i;
      }
    }
    this.anchorCounts[leastIdx] = (this.anchorCounts[leastIdx] ?? 0) + 1;
    const hub = this.hubs[leastIdx];
    if (hub) return { kind: 'anchor', hub };
    // Defensive: if no hubs exist we still need a beat to honour the
    // contract — synthesise an anchor at the origin so callers don't crash.
    const origin: PatrolHub = {
      getWorldPosition: (out) => out.set(0, 0, 0)
    };
    return { kind: 'anchor', hub: origin };
  }

  release(beat: PatrolBeat): void {
    if (beat.kind === 'anchor') {
      const idx = this.hubs.indexOf(beat.hub);
      if (idx >= 0) {
        this.anchorCounts[idx] = Math.max(0, (this.anchorCounts[idx] ?? 0) - 1);
      }
      return;
    }
    for (let i = 0; i < this.lanes.length; i++) {
      const lane = this.lanes[i];
      if (lane && lane.from === beat.from && lane.to === beat.to) {
        this.laneCounts[i] = Math.max(0, (this.laneCounts[i] ?? 0) - 1);
        return;
      }
    }
  }
}
